import React, { useRef, useState } from 'react';
import {
    Grid, Paper, Tabs, Tab, Box, Typography, Button, IconButton, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions, OutlinedInput, InputLabel,
    Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Chip,
    FormHelperText, Autocomplete, TextField, Select, MenuItem, LinearProgress
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import InputMask from 'react-input-mask';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentsIcon from '@mui/icons-material/Payments';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PageHeader from '../../../../components/page-header';
import BrazilianNumberInput from '../../../../components/brazilian-number-input';
import { useAmendmentDetailController } from './context/amendment-detail-controller';
import { useAmendmentDetailState } from './context/amendment-detail-context';
import { getAmendmentTypeLabel, getAmendmentStatus } from '../../../../model/sge/amendment/amendment-enums';
import { ACCOUNT_REPORT_STATUSES, getAccountReportStatus } from '../../../../model/sge/account-report/account-report-enums';
import { LEGAL_INSTRUMENT_TYPES, getLegalInstrumentTypeLabel } from '../../../../model/sge/transfer/transfer-enums';
import { formatDocument } from '../../../../utils/document';

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
    return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

function EmptyRow({ colSpan, message }: { colSpan: number; message: string }) {
    return (
        <TableRow>
            <TableCell colSpan={colSpan} align="center" sx={{ py: 4, color: 'text.secondary', border: 'none' }}>
                {message}
            </TableCell>
        </TableRow>
    );
}

function InfoField({ label, value }: { label: string; value?: React.ReactNode }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="body2" sx={{ mt: 0.25 }}>{value || '-'}</Typography>
        </Box>
    );
}

function ProgressStat({ label, current, total, color = 'primary' }: {
    label: string; current: number; total: number; color?: 'primary' | 'success' | 'warning';
}) {
    const fmt = (v: number) => `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    const pct = total ? (current / total) * 100 : 0;
    const over = total > 0 && current > total;
    return (
        <Box sx={{ mb: 1.5, '&:last-of-type': { mb: 0 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5, gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
                <Typography variant="caption" sx={{ whiteSpace: 'nowrap', fontWeight: over ? 700 : 400, color: over ? 'error.main' : 'text.secondary' }}>
                    {pct.toFixed(0)}%
                </Typography>
            </Box>
            <LinearProgress variant="determinate" value={Math.min(100, pct)} color={over ? 'error' : color} sx={{ height: 6, borderRadius: 3 }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {fmt(current)} de {fmt(total)}
            </Typography>
        </Box>
    );
}

export function AmendmentDetailComponent() {
    const state = useAmendmentDetailState();
    const ctrl = useAmendmentDetailController();
    const [expandedLiquidation, setExpandedLiquidation] = useState<number | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const fmtCurrency = (v?: number) => v != null ? `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-';
    const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('pt-BR') : '-';

    const statusInfo = getAmendmentStatus(state.amendment?.status);
    const parlamentar = state.parlamentarians.find((p) => p.id === state.amendment?.parlamentarId);
    const politicalParty = state.politicalParties.find((p) => p.id === state.amendment?.politicalPartyId);

    const paymentLiquidation = state.liquidations.find((l) => l.id === (state.paymentForm as any)?._liquidationId);
    const paymentEditingId = (state.paymentForm as any)?.id;
    const otherPaymentsTotal = state.payments
        .filter((p) => p.id !== paymentEditingId)
        .reduce((sum, p) => sum + (p.paidAmount || 0), 0);
    const projectedPaidTotal = otherPaymentsTotal + ((state.paymentForm as any)?.paidAmount || 0);
    const paymentExceedsLiquidation = !!paymentLiquidation && projectedPaidTotal > (paymentLiquidation.liquidatedAmount || 0);

    const liquidationEditingId = (state.liquidationForm as any)?.id;
    const otherLiquidationsTotal = state.liquidations
        .filter((l) => l.id !== liquidationEditingId)
        .reduce((sum, l) => sum + (l.liquidatedAmount || 0), 0);
    const projectedLiquidatedTotal = otherLiquidationsTotal + ((state.liquidationForm as any)?.liquidatedAmount || 0);
    const liquidationExceedsCommitment = !!state.selectedCommitment && projectedLiquidatedTotal > (state.selectedCommitment.committedAmount || 0);

    const commitmentEditingId = (state.commitmentForm as any)?.id;
    const existingCommitmentAggregate = state.commitments.find((c) => c.id === commitmentEditingId);
    const commitmentCommittedAmountLive = (state.commitmentForm as any)?.committedAmount || 0;

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title={state.amendment?.name || 'Emenda Parlamentar'}
                    subtitle={`Exercício ${state.amendment?.year || '-'} • ${getAmendmentTypeLabel(state.amendment?.type)}`}
                    breadcrumbs={[{ label: 'Emendas', href: '/emendas' }, { label: 'Detalhe' }]}
                    actions={
                        <>
                            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={ctrl.navigateToList}>Voltar</Button>
                            <Button variant="contained" startIcon={<EditIcon />} onClick={ctrl.navigateToEdit}>Editar</Button>
                        </>
                    }
                />
            </Grid>

            {/* Summary */}
            <Grid item xs={12}>
                <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }} elevation={3}>
                    <Grid container spacing={3}>

                        {/* Informações Gerais */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                                Informações Gerais
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={6} md={3}>
                            <Typography variant="caption" color="text.secondary">Status</Typography>
                            <Box sx={{ mt: 0.5 }}>
                                <Chip label={statusInfo?.label || '-'} color={statusInfo?.color || 'default'} size="small" />
                            </Box>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <InfoField label="Valor Total" value={<Typography variant="body1" sx={{ fontWeight: 600 }}>{fmtCurrency(state.amendment?.totalAmount)}</Typography>} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <InfoField label="Exercício" value={state.amendment?.year} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <InfoField label="Tipo" value={getAmendmentTypeLabel(state.amendment?.type)} />
                        </Grid>

                        {/* Vinculação */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, mt: 1, color: 'primary.main', fontWeight: 600 }}>
                                Vinculação
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <InfoField label="Parlamentar" value={parlamentar?.personName} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InfoField label="Partido Político" value={politicalParty ? `${politicalParty.abbreviation} - ${politicalParty.name}` : undefined} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InfoField label="Unidade Orçamentária" value={state.amendment?.budgetUnitName ? `${state.amendment.budgetUnitCode} - ${state.amendment.budgetUnitName}` : undefined} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InfoField label="Programa Orçamentário" value={state.amendment?.budgetProgramName ? `${state.amendment.budgetProgramCode} - ${state.amendment.budgetProgramName}` : undefined} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InfoField label="Ação Orçamentária" value={state.amendment?.budgetActionName ? `${state.amendment.budgetActionCode} - ${state.amendment.budgetActionName}` : undefined} />
                        </Grid>

                        {/* Detalhes */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, mt: 1, color: 'primary.main', fontWeight: 600 }}>
                                Detalhes
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12}>
                            <InfoField label="Descrição do Objeto" value={state.amendment?.objectDescription} />
                        </Grid>
                        <Grid item xs={12}>
                            <InfoField label="Base Legal" value={state.amendment?.legalBasis} />
                        </Grid>
                        <Grid item xs={12}>
                            <InfoField label="Finalidade Pública" value={state.amendment?.publicPurpose} />
                        </Grid>
                        <Grid item xs={12}>
                            <InfoField label="Justificativa Institucional" value={state.amendment?.institutionalJustification} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InfoField label="Público-Alvo" value={state.amendment?.targetAudience} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InfoField label="Local de Execução" value={state.amendment?.executionLocation} />
                        </Grid>

                    </Grid>
                </Paper>
            </Grid>

            {/* Tabs */}
            <Grid item xs={12}>
                <Paper elevation={3}>
                    <Tabs value={state.tabIndex} onChange={ctrl.handleTabChange} variant="scrollable" scrollButtons="auto">
                        <Tab icon={<ReceiptLongIcon fontSize="small" />} iconPosition="start" label={`Empenhos (${state.commitments.length})`} />
                        <Tab icon={<FactCheckIcon fontSize="small" />} iconPosition="start" label={`Prestação de Contas (${state.accountReports.length})`} />
                        <Tab icon={<AttachFileIcon fontSize="small" />} iconPosition="start" label={`Anexos (${state.attachments.length})`} />
                        <Tab icon={<SwapHorizIcon fontSize="small" />} iconPosition="start" label={`Transferências (${state.transfers.length})`} />
                    </Tabs>
                    <Box sx={{ p: 2 }}>

                        {/* TAB 0: Commitments */}
                        <TabPanel value={state.tabIndex} index={0}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="caption" color="text.secondary">Clique em um empenho para ver e gerenciar suas liquidações</Typography>
                                <Button variant="contained" startIcon={<AddIcon />} onClick={() => ctrl.openCommitmentDialog()}>Novo Empenho</Button>
                            </Box>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width={40} />
                                            <TableCell>Nº Empenho</TableCell>
                                            <TableCell>Data</TableCell>
                                            <TableCell>Fornecedor</TableCell>
                                            <TableCell>Documento</TableCell>
                                            <TableCell align="right">Valor Empenhado</TableCell>
                                            <TableCell align="right">Ações</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {state.commitments.length === 0 && <EmptyRow colSpan={7} message="Nenhum empenho cadastrado" />}
                                        {state.commitments.map((c) => {
                                            const isExpanded = state.selectedCommitment?.id === c.id;
                                            const totalLiquidated = isExpanded ? state.liquidations.reduce((sum, l) => sum + (l.liquidatedAmount || 0), 0) : 0;
                                            const liquidatedPct = c.committedAmount ? Math.min(100, (totalLiquidated / c.committedAmount) * 100) : 0;

                                            return (
                                                <React.Fragment key={c.id}>
                                                    <TableRow
                                                        hover
                                                        selected={isExpanded}
                                                        sx={{ cursor: 'pointer', '& td': { borderBottom: isExpanded ? 'none' : undefined } }}
                                                        onClick={() => ctrl.handleSelectCommitment(c)}
                                                    >
                                                        <TableCell>
                                                            <IconButton size="small">
                                                                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>{c.commitmentNumber || '-'}</TableCell>
                                                        <TableCell>{fmtDate(c.commitmentDate)}</TableCell>
                                                        <TableCell>{c.supplierName || '-'}</TableCell>
                                                        <TableCell>{formatDocument(c.supplierDocument) || '-'}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtCurrency(c.committedAmount)}</TableCell>
                                                        <TableCell align="right">
                                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); ctrl.openCommitmentDialog(c); }}><EditIcon fontSize="small" /></IconButton>
                                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); ctrl.handleDeleteCommitment(c.id); }}><DeleteIcon fontSize="small" /></IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                    {/* Liquidations panel */}
                                                    {isExpanded && (
                                                        <TableRow>
                                                            <TableCell colSpan={7} sx={{ p: 0, border: 'none' }}>
                                                                <Box sx={{ pl: { xs: 2, sm: 5 }, pr: 2, pb: 2.5, pt: 0.5 }}>
                                                                    <Paper
                                                                        variant="outlined"
                                                                        sx={{ borderRadius: 2, overflow: 'hidden', borderLeft: '3px solid', borderLeftColor: 'primary.main' }}
                                                                    >
                                                                        <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                    <ReceiptLongIcon fontSize="small" color="primary" />
                                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                                                        Liquidações ({state.liquidations.length})
                                                                                    </Typography>
                                                                                </Box>
                                                                                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => ctrl.openLiquidationDialog()}>
                                                                                    Nova Liquidação
                                                                                </Button>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                                                                                <LinearProgress
                                                                                    variant="determinate"
                                                                                    value={liquidatedPct}
                                                                                    sx={{ flex: 1, height: 6, borderRadius: 3 }}
                                                                                />
                                                                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                                                                    {fmtCurrency(totalLiquidated)} de {fmtCurrency(c.committedAmount)}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                        <Table size="small">
                                                                            <TableHead>
                                                                                <TableRow>
                                                                                    <TableCell width={40} />
                                                                                    <TableCell>Nº Liquidação</TableCell>
                                                                                    <TableCell>Data</TableCell>
                                                                                    <TableCell align="right">Valor Liquidado</TableCell>
                                                                                    <TableCell>Observações</TableCell>
                                                                                    <TableCell align="right">Ações</TableCell>
                                                                                </TableRow>
                                                                            </TableHead>
                                                                            <TableBody>
                                                                                {state.liquidations.length === 0 && <EmptyRow colSpan={6} message="Nenhuma liquidação registrada" />}
                                                                                {state.liquidations.map((l) => {
                                                                                    const isLiquidationExpanded = expandedLiquidation === l.id;
                                                                                    const totalPaid = isLiquidationExpanded ? state.payments.reduce((sum, p) => sum + (p.paidAmount || 0), 0) : 0;
                                                                                    const paidPct = l.liquidatedAmount ? Math.min(100, (totalPaid / l.liquidatedAmount) * 100) : 0;

                                                                                    return (
                                                                                        <React.Fragment key={l.id}>
                                                                                            <TableRow
                                                                                                hover
                                                                                                selected={isLiquidationExpanded}
                                                                                                sx={{ cursor: 'pointer', '& td': { borderBottom: isLiquidationExpanded ? 'none' : undefined } }}
                                                                                                onClick={() => { setExpandedLiquidation(isLiquidationExpanded ? null : l.id); if (!isLiquidationExpanded) ctrl.loadPayments(l.id); }}
                                                                                            >
                                                                                                <TableCell>
                                                                                                    <IconButton size="small">
                                                                                                        {isLiquidationExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                                                                    </IconButton>
                                                                                                </TableCell>
                                                                                                <TableCell sx={{ fontWeight: 600 }}>{l.liquidationNumber || '-'}</TableCell>
                                                                                                <TableCell>{fmtDate(l.liquidationDate)}</TableCell>
                                                                                                <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtCurrency(l.liquidatedAmount)}</TableCell>
                                                                                                <TableCell>{l.observations || '-'}</TableCell>
                                                                                                <TableCell align="right">
                                                                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); ctrl.openLiquidationDialog(l); }}><EditIcon fontSize="small" /></IconButton>
                                                                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); ctrl.handleDeleteLiquidation(l.id); }}><DeleteIcon fontSize="small" /></IconButton>
                                                                                                </TableCell>
                                                                                            </TableRow>
                                                                                            {/* Payments panel */}
                                                                                            {isLiquidationExpanded && (
                                                                                                <TableRow>
                                                                                                    <TableCell colSpan={6} sx={{ p: 0, border: 'none' }}>
                                                                                                        <Box sx={{ pl: { xs: 2, sm: 5 }, pr: 2, pb: 2, pt: 0.5 }}>
                                                                                                            <Paper
                                                                                                                variant="outlined"
                                                                                                                sx={{ borderRadius: 2, overflow: 'hidden', borderLeft: '3px solid', borderLeftColor: 'success.main' }}
                                                                                                            >
                                                                                                                <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.default' }}>
                                                                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                                                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                                                            <PaymentsIcon fontSize="small" color="success" />
                                                                                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                                                                                                Pagamentos ({state.payments.length})
                                                                                                                            </Typography>
                                                                                                                        </Box>
                                                                                                                        <Button size="small" variant="outlined" color="success" startIcon={<AddIcon />} onClick={() => ctrl.openPaymentDialog(l.id)}>
                                                                                                                            Novo Pagamento
                                                                                                                        </Button>
                                                                                                                    </Box>
                                                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                                                                                                                        <LinearProgress
                                                                                                                            variant="determinate"
                                                                                                                            value={paidPct}
                                                                                                                            color="success"
                                                                                                                            sx={{ flex: 1, height: 6, borderRadius: 3 }}
                                                                                                                        />
                                                                                                                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                                                                                                            {fmtCurrency(totalPaid)} de {fmtCurrency(l.liquidatedAmount)}
                                                                                                                        </Typography>
                                                                                                                    </Box>
                                                                                                                </Box>
                                                                                                                <Table size="small">
                                                                                                                    <TableHead>
                                                                                                                        <TableRow>
                                                                                                                            <TableCell>Nº Ordem</TableCell>
                                                                                                                            <TableCell>Data</TableCell>
                                                                                                                            <TableCell align="right">Valor Pago</TableCell>
                                                                                                                            <TableCell align="right">Ações</TableCell>
                                                                                                                        </TableRow>
                                                                                                                    </TableHead>
                                                                                                                    <TableBody>
                                                                                                                        {state.payments.length === 0 && <EmptyRow colSpan={4} message="Nenhum pagamento registrado" />}
                                                                                                                        {state.payments.map((p) => (
                                                                                                                            <TableRow key={p.id} hover>
                                                                                                                                <TableCell>{p.paymentOrderNumber || '-'}</TableCell>
                                                                                                                                <TableCell>{fmtDate(p.paymentDate)}</TableCell>
                                                                                                                                <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtCurrency(p.paidAmount)}</TableCell>
                                                                                                                                <TableCell align="right">
                                                                                                                                    <IconButton size="small" onClick={() => ctrl.openPaymentDialog(l.id, p)}><EditIcon fontSize="small" /></IconButton>
                                                                                                                                </TableCell>
                                                                                                                            </TableRow>
                                                                                                                        ))}
                                                                                                                    </TableBody>
                                                                                                                </Table>
                                                                                                            </Paper>
                                                                                                        </Box>
                                                                                                    </TableCell>
                                                                                                </TableRow>
                                                                                            )}
                                                                                        </React.Fragment>
                                                                                    );
                                                                                })}
                                                                            </TableBody>
                                                                        </Table>
                                                                    </Paper>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </TabPanel>

                        {/* TAB 1: Account Reports */}
                        <TabPanel value={state.tabIndex} index={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                <Button variant="contained" startIcon={<AddIcon />} onClick={() => ctrl.openAccountReportDialog()}>Nova Prestação de Contas</Button>
                            </Box>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead><TableRow><TableCell>Período</TableCell><TableCell>Status</TableCell><TableCell>Observações</TableCell><TableCell align="right">Ações</TableCell></TableRow></TableHead>
                                    <TableBody>
                                        {state.accountReports.length === 0 && <EmptyRow colSpan={4} message="Nenhuma prestação de contas cadastrada" />}
                                        {state.accountReports.map((r) => (
                                            <TableRow key={r.id} hover>
                                                <TableCell>{fmtDate(r.periodStartDate)} - {fmtDate(r.periodEndDate)}</TableCell>
                                                <TableCell>
                                                    {getAccountReportStatus(r.status) ? (
                                                        <Chip label={getAccountReportStatus(r.status)?.label} color={getAccountReportStatus(r.status)?.color} size="small" />
                                                    ) : '-'}
                                                </TableCell>
                                                <TableCell>{r.observations || '-'}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small" onClick={() => ctrl.openAccountReportDialog(r)}><EditIcon fontSize="small" /></IconButton>
                                                    <IconButton size="small" onClick={() => ctrl.handleDeleteAccountReport(r.id)}><DeleteIcon fontSize="small" /></IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </TabPanel>

                        {/* TAB 2: Attachments */}
                        <TabPanel value={state.tabIndex} index={2}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                <Button variant="contained" component="label" startIcon={<AttachFileIcon />}>
                                    Enviar Arquivo
                                    <input ref={fileInputRef} type="file" hidden onChange={(e) => { if (e.target.files?.[0]) { ctrl.handleUploadAttachment(e.target.files[0]); e.target.value = ''; } }} />
                                </Button>
                            </Box>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead><TableRow><TableCell>Nome</TableCell><TableCell>Tipo</TableCell><TableCell>Data</TableCell><TableCell align="right">Ações</TableCell></TableRow></TableHead>
                                    <TableBody>
                                        {state.attachments.length === 0 && <EmptyRow colSpan={4} message="Nenhum anexo enviado" />}
                                        {state.attachments.map((a) => (
                                            <TableRow key={a.id} hover>
                                                <TableCell>{a.title || a.originalFileName}</TableCell>
                                                <TableCell>{a.fileType}</TableCell>
                                                <TableCell>{fmtDate(a.createdAt)}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small" onClick={() => ctrl.handleDownloadAttachment(a.id, a.originalFileName || 'arquivo')}><DownloadIcon fontSize="small" /></IconButton>
                                                    <IconButton size="small" onClick={() => ctrl.handleDeleteAttachment(a.id)}><DeleteIcon fontSize="small" /></IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </TabPanel>

                        {/* TAB 3: Transfers */}
                        <TabPanel value={state.tabIndex} index={3}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                <Button variant="contained" startIcon={<AddIcon />} onClick={() => ctrl.openTransferDialog()}>Nova Transferência</Button>
                            </Box>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead><TableRow><TableCell>Empresa</TableCell><TableCell>CNPJ</TableCell><TableCell>Tipo Instrumento</TableCell><TableCell>Nº Instrumento</TableCell><TableCell>Data Assinatura</TableCell><TableCell align="right">Valor</TableCell><TableCell align="right">Ações</TableCell></TableRow></TableHead>
                                    <TableBody>
                                        {state.transfers.length === 0 && <EmptyRow colSpan={7} message="Nenhuma transferência cadastrada" />}
                                        {state.transfers.map((t) => (
                                            <TableRow key={t.id} hover>
                                                <TableCell>{t.companyName || '-'}</TableCell>
                                                <TableCell>{formatDocument(t.cnpj) || '-'}</TableCell>
                                                <TableCell>{getLegalInstrumentTypeLabel(t.legalInstrumentType)}</TableCell>
                                                <TableCell>{t.instrumentNumber || '-'}</TableCell>
                                                <TableCell>{fmtDate(t.signatureDate)}</TableCell>
                                                <TableCell align="right">{fmtCurrency(t.transferredAmount)}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small" onClick={() => ctrl.openTransferDialog(t)}><EditIcon fontSize="small" /></IconButton>
                                                    <IconButton size="small" onClick={() => ctrl.handleDeleteTransfer(t.id)}><DeleteIcon fontSize="small" /></IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </TabPanel>
                    </Box>
                </Paper>
            </Grid>

            {/* Commitment Dialog */}
            <Dialog open={state.showCommitmentDialog} onClose={ctrl.closeCommitmentDialog} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptLongIcon color="primary" fontSize="small" />
                    {(state.commitmentForm as any)?.id ? 'Editar Empenho' : 'Novo Empenho'}
                </DialogTitle>
                <DialogContent>
                    {existingCommitmentAggregate && (
                        <Box sx={{ mt: 0.5, mb: 2.5, p: 1.5, borderRadius: 2, bgcolor: 'background.default' }}>
                            <ProgressStat
                                label="Valor Liquidado"
                                current={existingCommitmentAggregate.totalLiquidatedValue || 0}
                                total={commitmentCommittedAmountLive}
                                color="warning"
                            />
                            <ProgressStat
                                label="Valor Pago"
                                current={existingCommitmentAggregate.totalPaidValue || 0}
                                total={commitmentCommittedAmountLive}
                                color="success"
                            />
                        </Box>
                    )}

                    <Grid container spacing={2.5} sx={{ mt: 0.5 }}>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary">Identificação</Typography>
                            <Divider sx={{ mt: 0.5 }} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputLabel required>Nº Empenho</InputLabel>
                            <OutlinedInput
                                fullWidth
                                name="commitmentNumber"
                                value={(state.commitmentForm as any)?.commitmentNumber || ''}
                                onChange={ctrl.handleCommitmentFormChange}
                                error={state.showCommitmentValidationErrors && !(state.commitmentForm as any)?.commitmentNumber}
                            />
                            {state.showCommitmentValidationErrors && !(state.commitmentForm as any)?.commitmentNumber && (
                                <FormHelperText error>Nº Empenho é obrigatório</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputLabel required>Data do Empenho</InputLabel>
                            <OutlinedInput
                                fullWidth
                                type="date"
                                name="commitmentDate"
                                value={(state.commitmentForm as any)?.commitmentDate || ''}
                                onChange={ctrl.handleCommitmentFormChange}
                                error={state.showCommitmentValidationErrors && !(state.commitmentForm as any)?.commitmentDate}
                            />
                            {state.showCommitmentValidationErrors && !(state.commitmentForm as any)?.commitmentDate && (
                                <FormHelperText error>Data do Empenho é obrigatória</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputLabel required>Data de Publicação</InputLabel>
                            <OutlinedInput
                                fullWidth
                                type="date"
                                name="publicationDate"
                                value={(state.commitmentForm as any)?.publicationDate || ''}
                                onChange={ctrl.handleCommitmentFormChange}
                                error={state.showCommitmentValidationErrors && !(state.commitmentForm as any)?.publicationDate}
                            />
                            {state.showCommitmentValidationErrors && !(state.commitmentForm as any)?.publicationDate && (
                                <FormHelperText error>Data de Publicação é obrigatória</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">Fornecedor e Pagamento</Typography>
                            <Divider sx={{ mt: 0.5 }} />
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <InputLabel required>Fornecedor</InputLabel>
                            <Autocomplete
                                fullWidth
                                options={state.suppliers}
                                value={state.suppliers.find((s) => s.id === (state.commitmentForm as any)?.supplierId) || null}
                                onChange={(_event, newValue) => ctrl.handleCommitmentFormChange({ target: { name: 'supplierId', value: newValue?.id ?? '' } })}
                                getOptionLabel={(option) => `${option.personName || ''}${option.cnpj || option.cpf ? ` - ${formatDocument(option.cnpj || option.cpf)}` : ''}`}
                                filterOptions={(options, filterState) => {
                                    const inputValue = filterState.inputValue.toLowerCase().trim();
                                    if (!inputValue) return options;
                                    return options.filter((option) =>
                                        option.personName?.toLowerCase().includes(inputValue) ||
                                        option.cnpj?.toLowerCase().includes(inputValue) ||
                                        option.cpf?.toLowerCase().includes(inputValue)
                                    );
                                }}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', py: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'left', width: '100%' }}>{option.personName}</Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'left', width: '100%' }}>{formatDocument(option.cnpj || option.cpf)}</Typography>
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Busque por nome ou CNPJ/CPF..."
                                        autoComplete="off"
                                        error={state.showCommitmentValidationErrors && !(state.commitmentForm as any)?.supplierId}
                                        inputProps={{
                                            ...params.inputProps,
                                            style: { textAlign: 'left' }
                                        }}
                                    />
                                )}
                                noOptionsText="Nenhum fornecedor encontrado"
                            />
                            {state.showCommitmentValidationErrors && !(state.commitmentForm as any)?.supplierId && (
                                <FormHelperText error>Fornecedor é obrigatório</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <InputLabel required>Valor Empenhado</InputLabel>
                            <BrazilianNumberInput
                                fullWidth
                                value={(state.commitmentForm as any)?.committedAmount}
                                onChange={(value) => ctrl.handleCommitmentFormChange({ target: { name: 'committedAmount', value } })}
                                decimals={2}
                                prefix="R$"
                                placeholder="0,00"
                                error={state.showCommitmentValidationErrors && !(state.commitmentForm as any)?.committedAmount}
                            />
                            {state.showCommitmentValidationErrors && !(state.commitmentForm as any)?.committedAmount && (
                                <FormHelperText error>Valor Empenhado é obrigatório</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <InputLabel>Conta Bancária</InputLabel>
                            <OutlinedInput fullWidth
                                multiline
                                rows={4} type="text" name="bankAccountUsed" value={(state.commitmentForm as any)?.bankAccountUsed || ''} onChange={ctrl.handleCommitmentFormChange} />
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">Vínculo</Typography>
                            <Divider sx={{ mt: 0.5 }} />
                        </Grid>
                        <Grid item xs={12}>
                            <InputLabel required>Objeto de Vínculo</InputLabel>
                            <OutlinedInput
                                fullWidth
                                multiline
                                rows={3}
                                name="objectLink"
                                value={(state.commitmentForm as any)?.objectLink || ''}
                                onChange={ctrl.handleCommitmentFormChange}
                                error={state.showCommitmentValidationErrors && !(state.commitmentForm as any)?.objectLink}
                            />
                            {state.showCommitmentValidationErrors && !(state.commitmentForm as any)?.objectLink && (
                                <FormHelperText error>Objeto de Vínculo é obrigatório</FormHelperText>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={ctrl.closeCommitmentDialog} disabled={ctrl.isSaving}>Cancelar</Button>
                    <LoadingButton variant="contained" onClick={ctrl.handleSaveCommitment} loading={ctrl.isSaving} disabled={ctrl.isSaving}>Salvar</LoadingButton>
                </DialogActions>
            </Dialog>

            {/* Liquidation Dialog */}
            <Dialog open={state.showLiquidationDialog} onClose={ctrl.closeLiquidationDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FactCheckIcon color="warning" fontSize="small" />
                    {(state.liquidationForm as any)?.id ? 'Editar Liquidação' : 'Nova Liquidação'}
                </DialogTitle>
                <DialogContent>
                    {state.selectedCommitment && (
                        <Box sx={{ mt: 0.5, mb: 2.5, p: 1.5, borderRadius: 2, bgcolor: 'background.default' }}>
                            <ProgressStat
                                label={`Empenho ${state.selectedCommitment.commitmentNumber ? `Nº ${state.selectedCommitment.commitmentNumber}` : ''}`}
                                current={projectedLiquidatedTotal}
                                total={state.selectedCommitment.committedAmount || 0}
                                color="warning"
                            />
                        </Box>
                    )}

                    <Grid container spacing={2.5}>
                        <Grid item xs={12} md={4}>
                            <InputLabel required>Nº Liquidação</InputLabel>
                            <OutlinedInput
                                fullWidth
                                name="liquidationNumber"
                                value={(state.liquidationForm as any)?.liquidationNumber || ''}
                                onChange={ctrl.handleLiquidationFormChange}
                                error={state.showLiquidationValidationErrors && !(state.liquidationForm as any)?.liquidationNumber}
                            />
                            {state.showLiquidationValidationErrors && !(state.liquidationForm as any)?.liquidationNumber && (
                                <FormHelperText error>Nº Liquidação é obrigatório</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputLabel required>Data</InputLabel>
                            <OutlinedInput
                                fullWidth
                                type="date"
                                name="liquidationDate"
                                value={(state.liquidationForm as any)?.liquidationDate || ''}
                                onChange={ctrl.handleLiquidationFormChange}
                                error={state.showLiquidationValidationErrors && !(state.liquidationForm as any)?.liquidationDate}
                            />
                            {state.showLiquidationValidationErrors && !(state.liquidationForm as any)?.liquidationDate && (
                                <FormHelperText error>Data é obrigatória</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputLabel required>Valor Liquidado</InputLabel>
                            <BrazilianNumberInput
                                fullWidth
                                value={(state.liquidationForm as any)?.liquidatedAmount}
                                onChange={(value) => ctrl.handleLiquidationFormChange({ target: { name: 'liquidatedAmount', value } })}
                                decimals={2}
                                prefix="R$"
                                placeholder="0,00"
                                error={state.showLiquidationValidationErrors && !(state.liquidationForm as any)?.liquidatedAmount}
                            />
                            {state.showLiquidationValidationErrors && !(state.liquidationForm as any)?.liquidatedAmount && (
                                <FormHelperText error>Valor Liquidado é obrigatório</FormHelperText>
                            )}
                            {!state.showLiquidationValidationErrors && liquidationExceedsCommitment && (
                                <FormHelperText error>O valor liquidado excede o valor do empenho</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <InputLabel>Observações</InputLabel>
                            <OutlinedInput fullWidth multiline rows={3} name="observations" value={(state.liquidationForm as any)?.observations || ''} onChange={ctrl.handleLiquidationFormChange} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={ctrl.closeLiquidationDialog} disabled={ctrl.isSaving}>Cancelar</Button>
                    <LoadingButton variant="contained" onClick={ctrl.handleSaveLiquidation} loading={ctrl.isSaving} disabled={ctrl.isSaving}>Salvar</LoadingButton>
                </DialogActions>
            </Dialog>

            {/* Payment Dialog */}
            <Dialog open={state.showPaymentDialog} onClose={ctrl.closePaymentDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PaymentsIcon color="success" fontSize="small" />
                    {(state.paymentForm as any)?.id ? 'Editar Pagamento' : 'Novo Pagamento'}
                </DialogTitle>
                <DialogContent>
                    {paymentLiquidation && (
                        <Box sx={{ mt: 0.5, mb: 2.5, p: 1.5, borderRadius: 2, bgcolor: 'background.default' }}>
                            <ProgressStat
                                label={`Liquidação ${paymentLiquidation.liquidationNumber ? `Nº ${paymentLiquidation.liquidationNumber}` : ''}`}
                                current={projectedPaidTotal}
                                total={paymentLiquidation.liquidatedAmount || 0}
                                color="success"
                            />
                        </Box>
                    )}

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <InputLabel required>Nº Ordem de Pagamento</InputLabel>
                            <OutlinedInput fullWidth name="paymentOrderNumber" value={(state.paymentForm as any)?.paymentOrderNumber || ''} onChange={ctrl.handlePaymentFormChange} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputLabel required>Data</InputLabel>
                            <OutlinedInput fullWidth type="date" name="paymentDate" value={(state.paymentForm as any)?.paymentDate || ''} onChange={ctrl.handlePaymentFormChange} />
                        </Grid>
                        <Grid item xs={12}>
                            <InputLabel required>Valor Pago</InputLabel>
                            <BrazilianNumberInput
                                fullWidth
                                value={(state.paymentForm as any)?.paidAmount}
                                onChange={(value) => ctrl.handlePaymentFormChange({ target: { name: 'paidAmount', value } })}
                                decimals={2}
                                prefix="R$"
                                placeholder="0,00"
                            />
                            {paymentExceedsLiquidation && (
                                <FormHelperText error>O valor pago excede o valor da liquidação</FormHelperText>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={ctrl.closePaymentDialog} disabled={ctrl.isSaving}>Cancelar</Button>
                    <LoadingButton variant="contained" onClick={ctrl.handleSavePayment} loading={ctrl.isSaving} disabled={ctrl.isSaving}>Salvar</LoadingButton>
                </DialogActions>
            </Dialog>

            {/* Transfer Dialog */}
            <Dialog open={state.showTransferDialog} onClose={ctrl.closeTransferDialog} maxWidth="md" fullWidth>
                <DialogTitle>{(state.transferForm as any)?.id ? 'Editar Transferência' : 'Nova Transferência'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2.5} sx={{ mt: 0.5 }}>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary">Entidade Convenente</Typography>
                            <Divider sx={{ mt: 0.5 }} />
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <InputLabel required>Razão Social</InputLabel>
                            <OutlinedInput
                                fullWidth
                                name="companyName"
                                value={(state.transferForm as any)?.companyName || ''}
                                onChange={ctrl.handleTransferFormChange}
                                error={state.showTransferValidationErrors && !(state.transferForm as any)?.companyName}
                            />
                            {state.showTransferValidationErrors && !(state.transferForm as any)?.companyName && (
                                <FormHelperText error>Razão Social é obrigatória</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputLabel>Nome Fantasia</InputLabel>
                            <OutlinedInput fullWidth name="tradeName" value={(state.transferForm as any)?.tradeName || ''} onChange={ctrl.handleTransferFormChange} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <InputLabel required>CNPJ</InputLabel>
                            <InputMask
                                mask="99.999.999/9999-99"
                                name="cnpj"
                                value={(state.transferForm as any)?.cnpj || ''}
                                onChange={ctrl.handleTransferFormChange}
                            >
                                <OutlinedInput
                                    fullWidth
                                    name="cnpj"
                                    autoComplete="off"
                                    error={state.showTransferValidationErrors && !(state.transferForm as any)?.cnpj}
                                />
                            </InputMask>
                            {state.showTransferValidationErrors && !(state.transferForm as any)?.cnpj && (
                                <FormHelperText error>CNPJ é obrigatório</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <InputLabel>Representante Legal</InputLabel>
                            <OutlinedInput fullWidth name="legalRepresentativeName" value={(state.transferForm as any)?.legalRepresentativeName || ''} onChange={ctrl.handleTransferFormChange} />
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">Instrumento Jurídico</Typography>
                            <Divider sx={{ mt: 0.5 }} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputLabel required>Tipo Instrumento</InputLabel>
                            <Select
                                fullWidth
                                name="legalInstrumentType"
                                value={(state.transferForm as any)?.legalInstrumentType ?? ''}
                                onChange={ctrl.handleTransferFormChange}
                                error={state.showTransferValidationErrors && !(state.transferForm as any)?.legalInstrumentType}
                            >
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {LEGAL_INSTRUMENT_TYPES.map((t) => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
                            </Select>
                            {state.showTransferValidationErrors && !(state.transferForm as any)?.legalInstrumentType && (
                                <FormHelperText error>Tipo Instrumento é obrigatório</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputLabel required>Nº Instrumento</InputLabel>
                            <OutlinedInput
                                fullWidth
                                name="instrumentNumber"
                                value={(state.transferForm as any)?.instrumentNumber || ''}
                                onChange={ctrl.handleTransferFormChange}
                                error={state.showTransferValidationErrors && !(state.transferForm as any)?.instrumentNumber}
                            />
                            {state.showTransferValidationErrors && !(state.transferForm as any)?.instrumentNumber && (
                                <FormHelperText error>Nº Instrumento é obrigatório</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputLabel required>Valor Transferido</InputLabel>
                            <BrazilianNumberInput
                                fullWidth
                                value={(state.transferForm as any)?.transferredAmount}
                                onChange={(value) => ctrl.handleTransferFormChange({ target: { name: 'transferredAmount', value } })}
                                decimals={2}
                                prefix="R$"
                                placeholder="0,00"
                                error={state.showTransferValidationErrors && !(state.transferForm as any)?.transferredAmount}
                            />
                            {state.showTransferValidationErrors && !(state.transferForm as any)?.transferredAmount && (
                                <FormHelperText error>Valor Transferido é obrigatório</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputLabel required>Data Assinatura</InputLabel>
                            <OutlinedInput
                                fullWidth
                                type="date"
                                name="signatureDate"
                                value={(state.transferForm as any)?.signatureDate || ''}
                                onChange={ctrl.handleTransferFormChange}
                                error={state.showTransferValidationErrors && !(state.transferForm as any)?.signatureDate}
                            />
                            {state.showTransferValidationErrors && !(state.transferForm as any)?.signatureDate && (
                                <FormHelperText error>Data Assinatura é obrigatória</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputLabel required>Início Vigência</InputLabel>
                            <OutlinedInput
                                fullWidth
                                type="date"
                                name="effectiveStartDate"
                                value={(state.transferForm as any)?.effectiveStartDate || ''}
                                onChange={ctrl.handleTransferFormChange}
                                error={state.showTransferValidationErrors && !(state.transferForm as any)?.effectiveStartDate}
                            />
                            {state.showTransferValidationErrors && !(state.transferForm as any)?.effectiveStartDate && (
                                <FormHelperText error>Início Vigência é obrigatório</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputLabel required>Fim Vigência</InputLabel>
                            <OutlinedInput
                                fullWidth
                                type="date"
                                name="effectiveEndDate"
                                value={(state.transferForm as any)?.effectiveEndDate || ''}
                                onChange={ctrl.handleTransferFormChange}
                                error={state.showTransferValidationErrors && !(state.transferForm as any)?.effectiveEndDate}
                            />
                            {state.showTransferValidationErrors && !(state.transferForm as any)?.effectiveEndDate && (
                                <FormHelperText error>Fim Vigência é obrigatório</FormHelperText>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={ctrl.closeTransferDialog} disabled={ctrl.isSaving}>Cancelar</Button>
                    <LoadingButton variant="contained" onClick={ctrl.handleSaveTransfer} loading={ctrl.isSaving} disabled={ctrl.isSaving}>Salvar</LoadingButton>
                </DialogActions>
            </Dialog>

            {/* Account Report Dialog */}
            <Dialog open={state.showAccountReportDialog} onClose={ctrl.closeAccountReportDialog} maxWidth="md" fullWidth>
                <DialogTitle>{(state.accountReportForm as any)?.id ? 'Editar Prestação de Contas' : 'Nova Prestação de Contas'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2.5} sx={{ mt: 0.5 }}>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary">Período</Typography>
                            <Divider sx={{ mt: 0.5 }} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputLabel required>Início do Período</InputLabel>
                            <OutlinedInput fullWidth type="date" name="periodStartDate" value={(state.accountReportForm as any)?.periodStartDate || ''} onChange={ctrl.handleAccountReportFormChange} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputLabel required>Fim do Período</InputLabel>
                            <OutlinedInput fullWidth type="date" name="periodEndDate" value={(state.accountReportForm as any)?.periodEndDate || ''} onChange={ctrl.handleAccountReportFormChange} />
                        </Grid>

                        {(state.accountReportForm as any)?.id && (
                            <>
                                <Grid item xs={12} sx={{ mt: 1 }}>
                                    <Typography variant="subtitle2" color="text.secondary">Acompanhamento</Typography>
                                    <Divider sx={{ mt: 0.5 }} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <InputLabel>Data de Envio</InputLabel>
                                    <OutlinedInput fullWidth type="date" name="submissionDate" value={(state.accountReportForm as any)?.submissionDate || ''} onChange={ctrl.handleAccountReportFormChange} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <InputLabel>Data de Revisão</InputLabel>
                                    <OutlinedInput fullWidth type="date" name="reviewDate" value={(state.accountReportForm as any)?.reviewDate || ''} onChange={ctrl.handleAccountReportFormChange} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <InputLabel>Status</InputLabel>
                                    <Select fullWidth name="status" value={(state.accountReportForm as any)?.status ?? ''} onChange={ctrl.handleAccountReportFormChange}>
                                        <MenuItem value=""><em>Selecionar</em></MenuItem>
                                        {ACCOUNT_REPORT_STATUSES.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
                                    </Select>
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">Observações</Typography>
                            <Divider sx={{ mt: 0.5 }} />
                        </Grid>
                        <Grid item xs={12}>
                            <InputLabel>Observações</InputLabel>
                            <OutlinedInput fullWidth multiline rows={3} name="observations" value={(state.accountReportForm as any)?.observations || ''} onChange={ctrl.handleAccountReportFormChange} />
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">Anexos</Typography>
                            <Divider sx={{ mt: 0.5 }} />
                        </Grid>
                        {(state.accountReportForm as any)?.id && (
                            <Grid item xs={12}>
                                {state.accountReportAttachments.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">Nenhum arquivo enviado ainda</Typography>
                                ) : (
                                    state.accountReportAttachments.map((a) => (
                                        <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                                            <Typography variant="body2">{a.title || a.originalFileName}</Typography>
                                            <Box>
                                                <IconButton size="small" onClick={() => ctrl.handleDownloadAccountReportAttachment(a.id, a.originalFileName || 'arquivo')}><DownloadIcon fontSize="small" /></IconButton>
                                                <IconButton size="small" onClick={() => ctrl.handleDeleteAccountReportAttachment(a.id)}><DeleteIcon fontSize="small" /></IconButton>
                                            </Box>
                                        </Box>
                                    ))
                                )}
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Button variant="outlined" component="label" startIcon={<AttachFileIcon />}>
                                Selecionar Arquivos ({state.accountReportFiles.length})
                                <input type="file" multiple hidden onChange={ctrl.handleAccountReportFilesChange} />
                            </Button>
                            {state.accountReportFiles.length > 0 && (
                                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                    {state.accountReportFiles.map((f) => f.name).join(', ')}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={ctrl.closeAccountReportDialog} disabled={ctrl.isSaving}>Cancelar</Button>
                    <LoadingButton variant="contained" onClick={ctrl.handleSaveAccountReport} loading={ctrl.isSaving} disabled={ctrl.isSaving}>Salvar</LoadingButton>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
